/**
 * Раздел «Экономика» страницы «Аналитика» (redesign по макету): заголовок
 * секции с линией и иконкой + ряд стат-карточек + карточка
 * блок «Экономика региона» (четыре отрасли с ИФО, US-35.1 FR-4) + блок
 * «Цены, труд и доходы населения» (инфляция и рынок труда, FR-5/FR-6) +
 * подраздел «Развитие МСБ» (pie «зарегистрированные субъекты по видам
 * деятельности»).
 *
 * Виджет «Экономическое развитие» (топ-4 отрасли СЭР) заменён блоком
 * «Экономика региона» (FR-4.2), подвиджет «Финансово-кредитная поддержка»
 * снят со страницы (FR-1) — не «нет данных», а отсутствие блока. Поле
 * `msb.financial` выдача ещё возвращает, экран его не показывает.
 *
 * Источник данных — гибрид: демо-КАТО (HARD_DATA_KATOS)
 * → мок hardData/analyticsEconomy.json; остальные КАТО (по выбранному
 * в сайдбаре) — один запрос /api/public/analytics/economy (DP-127,
 * US-04.3):
 *  - стат-карточки — plashka (ЗП, ИФО+объём инвестиций/промышленности,
 *    исполнение бюджета, налоговые поступления факт);
 *  - «Экономика региона» и «Цены, труд и доходы» — СВОИ операции
 *    `/analytics/economy/region-economy` и `/prices-labor` (US-36.1): у них
 *    другая периодичность и другие источники, и в агрегат подраздела они
 *    не дописаны;
 *  - «Освоение бюджета» — budget_execution (итого / расходы районов /
 *    расходы МСУ; «факт» = план × % освоения);
 *  - «Доходы и налоговые поступления» — первая строка income_tax
 *    (налоговые / неналоговые / продажа осн. капитала, факт/план за
 *    период; исполнение = факт / план); порядок строк фиксированный —
 *    налоговые → неналоговые → продажа основного капитала;
 *  - «Развитие МСБ» — msb: pie «Зарегистрированные субъекты МСБ по видам
 *    деятельности» (доли — activities; registered_count — плашкой над
 *    разбивкой, действующие — подписью в шапке).
 */
import { Coins } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart } from 'recharts';

import { cn } from '../../../lib/cn';

import { LegalEntityTaxes } from './LegalEntityTaxes';
import { PricesLaborBlock } from './PricesLaborBlock';
import { RegionEconomyBlock } from './RegionEconomyBlock';
import { StatCard } from '../../../components/widgets/StatCard';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../components/ui/chart';
import analyticsEconomy from '../../../hardData/analyticsEconomy.json';
import {
  getAnalyticsEconomy,
  getPricesLabor,
  getRegionEconomy,
} from '../../../requests/analytics.request';
import { ModuleGate } from '../../../components/common/ModuleGate';
import { MODULES } from '../../../consts/modules';
import { useModulePresent } from '../../../hooks/useModulePresent';
import { useAnalyticsSection } from '../useAnalyticsSection';
import { useKato } from '../../../contexts/KatoContext';
import { useYear } from '../../../contexts/YearContext';
import type {
  EconomyAnalyticsResponse,
  PricesLaborResponse,
  RegionEconomyResponse,
} from '../../../types/analytics';

/** Таблицы бюджета — отдельные виджеты витрины (US-09 FR-1). */
const BUDGET_MODULE: Record<'execution' | 'revenue', string> = {
  execution: MODULES.analytics.economyBudgetExecution,
  revenue: MODULES.analytics.economyBudgetRevenue,
};

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);
const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);

const fmtTrend = (v: number): string => `${v > 0 ? '+' : ''}${v}`;

interface EconCardVM {
  key: string;
  value: string;
  unit: string | null;
  trend: { text: string } | null;
  footnotes: Array<{ text: string; bold?: boolean }>;
}

/** Палитра долей pie «виды деятельности МСБ» — как соседние pie-чарты. */
const MSB_COLORS = [
  '#4A63E7',
  '#2C9C42',
  '#D89B1F',
  '#8FA3F2',
  '#A620F0',
  '#E87BA4',
  '#5FC2C9',
  '#F2884B',
];

/** Доля pie «Зарегистрированные субъекты МСБ по видам деятельности». */
interface MsbSegmentVM {
  key: string;
  name: string;
  count: number;
  color: string;
}

/**
 * Порядок строк «Доходов и налоговых поступлений»: налоговые →
 * неналоговые → поступления от продажи основного капитала (остальные
 * категории — в конце в порядке ответа). Названия приходят готовыми
 * строками из analytics-БД, поэтому сопоставляем по ключевым словам.
 */
const incomeRowOrder = (name: string): number => {
  const n = name.toLowerCase();
  if (n.includes('неналог') || n.includes('салықтық емес')) return 1;
  if (n.includes('налог') || n.includes('салық')) return 0;
  if (n.includes('продаж') || n.includes('сату')) return 2;
  return 3;
};

interface BudgetRowVM {
  key: string;
  name: string;
  execution: string;
  /** Годовой план (только у строк из analytics-БД, DP-134). */
  planYearly: string | null;
  plan: string;
  fact: string;
  highlight: boolean;
}

const pct = (v: number | null): string => (v !== null ? `${fmt1(v)}%` : '—');

interface EconomySectionProps {
  /** Режим витрины «Все» (US-04.1): только ключевые виджеты — «ТОП-4»,
   * «Освоение бюджета», «Доходы и налоговые поступления» (FR-2.2). */
  summary?: boolean;
}

export const EconomySection = ({
  summary = false,
}: EconomySectionProps = {}) => {
  const { t, i18n } = useTranslation();
  // Плашка ключевых показателей — отдельный виджет витрины (US-09 FR-1).
  const showPlashka = useModulePresent(MODULES.analytics.economyPlashka);
  // Блоки первой волны — свои элементы витрины: выключенный не рендерится
  // и данных не запрашивает (US-09 FR-1). Выдача дублирует решение полем
  // `widget_enabled`, и блок уважает оба.
  const showRegion = useModulePresent(MODULES.analytics.economyRegion);
  const showPricesLabor = useModulePresent(MODULES.analytics.economyPricesLabor);
  const isKz = i18n.resolvedLanguage === 'kz';
  const { cards, budget, msb, legalTaxes: mockLegalTaxes } = analyticsEconomy;

  const { data, loading, isHardKato } =
    useAnalyticsSection<EconomyAnalyticsResponse>(
      'economy',
      getAnalyticsEconomy,
    );

  // Блоки первой волны US-35.1 — СВОИ операции (US-36.1 FR-1.5): у них
  // другая периодичность и другие источники, и в агрегат подраздела они не
  // дописаны. Запрашиваются и на демо-КАТО: витрины отвечают по реальным
  // областным кодам, и мок был бы хуже живых данных.
  const { selectedKato } = useKato();
  const { selectedYear } = useYear();
  const [regionEconomy, setRegionEconomy] =
    useState<RegionEconomyResponse | null>(null);
  const [regionLoading, setRegionLoading] = useState(false);
  const [pricesLabor, setPricesLabor] = useState<PricesLaborResponse | null>(
    null,
  );
  const [pricesLoading, setPricesLoading] = useState(false);

  useEffect(() => {
    if (selectedKato === null) return;
    let cancelled = false;
    if (showRegion) {
      setRegionLoading(true);
      getRegionEconomy(selectedKato, selectedYear)
        .then((response) => {
          if (!cancelled) setRegionEconomy(response);
        })
        // Эндпоинта ещё нет на стенде / 503 источника — блок показывает
        // пустое состояние, страница не падает.
        .catch((err) => {
          if (!cancelled) {
            console.error('economy: region-economy request failed', err);
            setRegionEconomy(null);
          }
        })
        .finally(() => {
          if (!cancelled) setRegionLoading(false);
        });
    }
    if (showPricesLabor) {
      setPricesLoading(true);
      getPricesLabor(selectedKato, selectedYear)
        .then((response) => {
          if (!cancelled) setPricesLabor(response);
        })
        .catch((err) => {
          if (!cancelled) {
            console.error('economy: prices-labor request failed', err);
            setPricesLabor(null);
          }
        })
        .finally(() => {
          if (!cancelled) setPricesLoading(false);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [selectedKato, selectedYear, showRegion, showPricesLabor]);

  // --- Вью-модели: мок для демо-КАТО, /analytics/economy для остальных ---

  const plashka = data?.plashka;

  // Карточка «ИФО + объём» (инвестиции / промышленность): значение — ИФО,
  // сноска — объём в млн тг.
  const ifoCard = (
    key: string,
    ifo: number | null,
    val: number | null,
  ): EconCardVM => ({
    key,
    value: ifo !== null ? `${fmt1(ifo)} %` : '—',
    unit: t('digitalPassport.serTable.columnIfo'),
    trend: null,
    footnotes:
      val !== null
        ? [{ text: `${fmt1(val)} ${t('units.mlnTenge')}`, bold: true }]
        : [],
  });

  const cardVMs: EconCardVM[] = isHardKato
    ? cards.map((card) => ({
        key: card.key,
        value: card.value,
        unit: card.unit ? (isKz ? card.unit.kz : card.unit.ru) : null,
        trend: card.trend != null ? { text: fmtTrend(card.trend) } : null,
        footnotes: card.footnotes.map((note) => ({
          text: isKz ? note.kz : note.ru,
          bold: note.bold,
        })),
      }))
    : [
        {
          key: 'avgSalary',
          value: plashka?.avg_salary != null ? fmt(plashka.avg_salary) : '—',
          unit: t('units.tenge'),
          trend: null,
          footnotes: [],
        },
        ifoCard(
          'investments',
          plashka?.investment_ifo ?? null,
          plashka?.investment_val ?? null,
        ),
        {
          key: 'budgetExecution',
          value: pct(plashka?.budget_execution_pct ?? null),
          unit: null,
          trend: null,
          footnotes: [],
        },
        {
          key: 'taxRevenue',
          value:
            plashka?.tax_revenues_fact != null
              ? fmt1(plashka.tax_revenues_fact)
              : '—',
          unit: plashka?.tax_revenues_fact != null ? t('units.mlnTenge') : null,
          trend: null,
          footnotes: [],
        },
        ifoCard(
          'industrialProduction',
          plashka?.industry_ifo ?? null,
          plashka?.industry_val ?? null,
        ),
      ];

  const amt = (v: number | null): string => (v !== null ? fmt(v) : '—');
  // Исполнение = факт / план за период (нет пары → «—»).
  const execPct = (fact: number | null, plan: number | null): string =>
    fact !== null && plan !== null && plan !== 0
      ? pct((fact / plan) * 100)
      : '—';

  // «Доходы и налоговые поступления»: демо-КАТО — мок; остальные —
  // budget_income из analytics-БД (DP-134: налоговые / неналоговые /
  // продажа основного капитала), fallback — legacy income_tax.
  const revenueRows: BudgetRowVM[] = isHardKato
    ? budget.revenue.rows.map((row) => ({
        key: row.key,
        name: isKz ? row.name.kz : row.name.ru,
        execution: row.execution,
        planYearly: null,
        plan: row.plan,
        fact: row.fact,
        highlight: row.highlight,
      }))
    : (() => {
        // Порядок категорий из БД не гарантирован — выстраиваем в
        // привычный: налоговые → неналоговые → продажа осн. капитала.
        const income = [...(data?.budget_income ?? [])].sort(
          (a, b) => incomeRowOrder(a.ktk_name) - incomeRowOrder(b.ktk_name),
        );
        if (income.length > 0) {
          return income.map((row, index) => ({
            key: `income-${index}`,
            // Названия категорий приходят готовыми строками из analytics-БД.
            name: row.ktk_name,
            execution: pct(row.execution_pct),
            planYearly: amt(row.plan_yearly),
            plan: amt(row.plan_period),
            fact: amt(row.fact_period),
            highlight: false,
          }));
        }
        const it = data?.income_tax[0];
        if (!it) return [];
        const defs: Array<[string, number | null, number | null]> = [
          ['tax', it.tax_revenues_fact, it.tax_revenues_plan],
          ['nonTax', it.non_tax_revenues_fact, it.non_tax_revenues_plan],
          ['assetSales', it.fixed_assets_sale_fact, it.fixed_assets_sale_plan],
        ];
        return defs.flatMap(([key, fact, plan]) => {
          if (fact === null && plan === null) return [];
          return [
            {
              key,
              name: t(`analytics.economy.budget.rows.${key}`),
              execution: execPct(fact, plan),
              planYearly: null,
              plan: amt(plan),
              fact: amt(fact),
              highlight: false,
            },
          ];
        });
      })();

  // «Освоение бюджета»: демо-КАТО — мок; остальные — budget_expenditure
  // из analytics-БД (DP-134: Итого / Район / МСУ, «Итого» первой строкой
  // считает бэк — выделяем), fallback — legacy budget_execution.
  const executionRows: BudgetRowVM[] = isHardKato
    ? budget.execution.rows.map((row) => ({
        key: row.key,
        name: isKz ? row.name.kz : row.name.ru,
        execution: row.execution,
        planYearly: null,
        plan: row.plan,
        fact: row.fact,
        highlight: row.highlight,
      }))
    : (() => {
        const expenditure = data?.budget_expenditure ?? [];
        if (expenditure.length > 0) {
          return expenditure.map((row, index) => ({
            key: `expenditure-${index}`,
            name: row.name,
            execution: pct(row.execution_pct),
            planYearly: amt(row.plan_yearly),
            plan: amt(row.plan_period),
            fact: amt(row.fact_period),
            highlight: index === 0,
          }));
        }
        const be = data?.budget_execution;
        if (!be) return [];
        const defs: Array<[string, number | null, number | null, boolean]> = [
          ['total', be.total_cur_year_budget, be.total_budget_processing, true],
          [
            'districts',
            be.cur_year_dev_budget,
            be.dev_budget_processing,
            false,
          ],
          [
            'msu',
            be.cur_year_active_budget,
            be.active_budget_processing,
            false,
          ],
        ];
        return defs.flatMap(([key, plan, processing, highlight]) => {
          if (plan === null && processing === null) return [];
          const fact =
            plan !== null && processing !== null
              ? (plan * processing) / 100
              : null;
          return [
            {
              key,
              name: t(`analytics.economy.budget.rows.${key}`),
              execution: pct(processing),
              planYearly: null,
              plan: plan !== null ? fmt1(plan) : '—',
              fact: fact !== null ? fmt1(fact) : '—',
              highlight,
            },
          ];
        });
      })();


  const msbSegments: MsbSegmentVM[] = (
    isHardKato
      ? msb.directions.rows.map((row) => ({
          key: row.key,
          name: isKz ? row.name.kz : row.name.ru,
          count: row.count,
        }))
      : (data?.msb?.activities ?? []).flatMap((row) =>
          row.count === null
            ? []
            : [{ key: row.activity, name: row.activity, count: row.count }],
        )
  )
    .sort((a, b) => b.count - a.count)
    .map((row, index) => ({
      ...row,
      color: MSB_COLORS[index % MSB_COLORS.length],
    }));

  // Нулевые виды остаются в легенде, но не рисуются долями.
  const msbPieSegments = msbSegments.filter((segment) => segment.count > 0);

  // Центр pie — общее количество зарегистрированных субъектов МСБ
  // (subjects.registered_count; нет значения — сумма долей).
  const msbSegmentsSum = msbSegments.reduce((acc, row) => acc + row.count, 0);
  const msbRegistered = isHardKato
    ? msb.subjects.registeredCount
    : (data?.msb?.subjects?.registered_count ?? msbSegmentsSum);
  // Действующие субъекты — подписью в шапке карточки (отдельного виджета
  // под них больше нет).
  const msbActive = isHardKato
    ? msb.subjects.activeCount
    : (data?.msb?.subjects?.active_count ?? null);

  const msbChartConfig = Object.fromEntries(
    msbSegments.map((segment) => [
      segment.key,
      { label: segment.name, color: segment.color },
    ]),
  ) satisfies ChartConfig;

  return (
    <section className="space-y-4" data-testid="analytics-economy">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-xl font-extrabold">
          {t('analytics.economy.title')}
        </h2>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <Coins
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {/* Пока грузятся данные с бэка — лоадер вместо виджетов. */}
      {loading ? (
        <BlockLoader />
      ) : (
        <>
          {!summary && showPlashka && (
            <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-5">
              {cardVMs.map((card) => (
                <StatCard
                  key={card.key}
                  testId={`analytics-card-${card.key}`}
                  title={t(`analytics.economy.cards.${card.key}`)}
                  value={card.value}
                  unit={card.unit}
                  trend={card.trend}
                  footnotes={card.footnotes}
                />
              ))}
            </div>
          )}

          {/* FR-4.2: блок «Экономика региона» ЗАМЕНИЛ виджет
              «Экономическое развитие» (топ-4 отрасли СЭР) — оба показывали
              отраслевые объёмы с ИФО, и держать их одновременно значит
              показывать одно и то же дважды. */}
          {showRegion && (
            <RegionEconomyBlock data={regionEconomy} loading={regionLoading} />
          )}

          {!summary && showPricesLabor && (
            <PricesLaborBlock data={pricesLabor} loading={pricesLoading} />
          )}

          <div className="grid gap-3.5 xl:grid-cols-2">
            {(['execution', 'revenue'] as const).map((tableKey) => {
              const table = budget[tableKey];
              const rows: BudgetRowVM[] =
                tableKey === 'revenue' ? revenueRows : executionRows;
              // Годовой план есть только у строк из analytics-БД (DP-134).
              const hasPlanYearly = rows.some((row) => row.planYearly !== null);
              return (
                <ModuleGate key={tableKey} id={BUDGET_MODULE[tableKey]}>
                <Card className="shadow-card">
                  <CardHeader className="flex-row items-baseline justify-between gap-4 space-y-0">
                    <CardTitle>
                      {t(`analytics.economy.budget.${tableKey}Title`)}
                    </CardTitle>
                    <span className="shrink-0 text-sm text-text-faint">
                      {isHardKato
                        ? isKz
                          ? table.period.kz
                          : table.period.ru
                        : t('units.mlnTenge')}
                    </span>
                  </CardHeader>
                  <CardContent>
                    {rows.length === 0 ? (
                      <p
                        data-testid="block-placeholder"
                        className="text-sm lowercase text-text-muted"
                      >
                        {t('common.noData')}
                      </p>
                    ) : (
                      <div data-testid={`analytics-budget-${tableKey}`}>
                        <div className="flex items-center gap-3 px-3 pb-2 text-sm leading-tight text-text-faint">
                          <span className="min-w-0 flex-1" />
                          <span className="w-24 shrink-0 text-right">
                            {t('analytics.economy.budget.columnExecution')}
                          </span>
                          {hasPlanYearly && (
                            <span className="w-20 shrink-0 text-right">
                              {t('analytics.economy.budget.columnPlanYearly')}
                            </span>
                          )}
                          <span className="w-20 shrink-0 text-right">
                            {t('analytics.economy.budget.columnPlan')}
                          </span>
                          <span className="w-20 shrink-0 text-right">
                            {t('analytics.economy.budget.columnFact')}
                          </span>
                        </div>
                        <ul>
                          {rows.map((row) => (
                            <li
                              key={row.key}
                              data-testid={`analytics-budget-${tableKey}-${row.key}`}
                              className={cn(
                                'flex items-center gap-3 px-3 py-4 text-sm',
                                row.highlight
                                  ? 'rounded-lg bg-surface-muted/60'
                                  : 'border-b border-surface-border last:border-b-0 last:pb-0',
                              )}
                            >
                              <span
                                className={cn(
                                  'min-w-0 flex-1 leading-snug',
                                  row.highlight
                                    ? 'font-extrabold'
                                    : 'font-semibold',
                                )}
                              >
                                {row.name}
                              </span>
                              <span className="w-24 shrink-0 text-right font-bold tabular-nums text-chart-indigo">
                                {row.execution}
                              </span>
                              {hasPlanYearly && (
                                <span className="w-20 shrink-0 text-right font-bold tabular-nums">
                                  {row.planYearly ?? '—'}
                                </span>
                              )}
                              <span className="w-20 shrink-0 text-right font-extrabold tabular-nums">
                                {row.plan}
                              </span>
                              <span className="w-20 shrink-0 text-right font-bold tabular-nums text-brand-600">
                                {row.fact}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
                </ModuleGate>
              );
            })}
          </div>

          {!summary && (
            <>
              {/* «Налоговые поступления юридических лиц» (FR-3a) — после бюджетных
          виджетов, перед «Развитием МСБ». */}
              {/* Демо-КАТО — мок формы 2-47; остальные — ресты
                  /analytics/legal-entity-taxes* (виджет грузит их сам). */}
              <LegalEntityTaxes demo={isHardKato ? mockLegalTaxes : null} />

              <div className="flex items-center gap-4 pt-2">
                <h3 className="shrink-0 text-lg font-extrabold">
                  {t('analytics.economy.msb.title')}
                </h3>
                <div
                  className="h-px flex-1 bg-surface-border"
                  aria-hidden="true"
                />
              </div>

              {/* Субъекты МСБ и их виды деятельности — один виджет: pie с долями
          видов деятельности (количество — в легенде и в тултипе по
          наведению); общее число зарегистрированных субъектов — в центре
          pie и однострочной плашкой над разбивкой; единицы колонок
          («ед.» / «%») — над столбцами легенды (замечания ревью). */}
              <ModuleGate id={MODULES.analytics.economyMsbSubjects}>
              <Card className="shadow-card">
                <CardHeader className="flex-row flex-wrap items-baseline justify-between gap-2 space-y-0">
                  <CardTitle>
                    {t('analytics.economy.msb.subjects.title')}
                  </CardTitle>
                  {msbActive !== null && (
                    <span className="shrink-0 text-sm text-text-faint">
                      {t('analytics.economy.msb.subjects.activeLabel')}:{' '}
                      <span
                        className="font-bold text-text"
                        data-testid="analytics-msb-active"
                      >
                        {fmt(msbActive)}
                      </span>
                    </span>
                  )}
                </CardHeader>
                <CardContent>
                  {msbSegments.length === 0 ? (
                    <p
                      data-testid="block-placeholder"
                      className="text-sm lowercase text-text-muted"
                    >
                      {t('common.noData')}
                    </p>
                  ) : (
                    <div
                      className="flex flex-wrap items-center justify-center gap-6"
                      data-testid="analytics-msb-subjects"
                    >
                      <div className="relative w-full max-w-[220px] shrink-0">
                        <ChartContainer
                          config={msbChartConfig}
                          className="aspect-square"
                        >
                          <PieChart>
                            <ChartTooltip
                              cursor={false}
                              content={<ChartTooltipContent hideLabel />}
                            />
                            <Pie
                              data={msbPieSegments.map((segment) => ({
                                key: segment.key,
                                value: segment.count,
                              }))}
                              dataKey="value"
                              nameKey="key"
                              innerRadius="58%"
                              outerRadius="98%"
                              strokeWidth={2}
                            >
                              {msbPieSegments.map((segment) => (
                                <Cell key={segment.key} fill={segment.color} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ChartContainer>
                        {/* Общее число зарегистрированных — в центре pie
                    (замечание ревью). */}
                        <div
                          className="pointer-events-none absolute inset-0 flex items-center justify-center"
                          aria-hidden="true"
                        >
                          <span className="text-2xl font-extrabold tabular-nums">
                            {fmt(msbRegistered)}
                          </span>
                        </div>
                      </div>

                      <div className="flex min-w-56 flex-1 flex-col gap-4">
                        {/* Общее число зарегистрированных субъектов — однострочной
                    плашкой над разбивкой (замечание ревью). */}
                        <div
                          className="flex items-baseline gap-2 rounded-lg bg-brand-50 px-4 py-2.5 text-sm"
                          data-testid="analytics-msb-registered"
                        >
                          <span className="min-w-0 leading-snug text-text-muted">
                            {t(
                              'analytics.economy.msb.subjects.registeredLabel',
                            )}
                          </span>
                        </div>

                        <div className="min-w-0">
                          {/* Единицы измерения — над столбцами легенды. */}
                          <div className="flex items-baseline gap-2 pb-1.5 text-xs text-text-faint">
                            <span className="min-w-0 flex-1" />
                            <span className="w-16 shrink-0 text-right">
                              {t('units.pieces')}
                            </span>
                            <span className="w-12 shrink-0 text-right">%</span>
                          </div>
                          <ul
                            className="flex flex-col gap-2"
                            data-testid="analytics-msb-directions"
                          >
                            {msbSegments.map((segment) => (
                              <li
                                key={segment.key}
                                data-testid={`analytics-msb-direction-${segment.key}`}
                                className="flex items-baseline gap-2 text-sm"
                              >
                                <span
                                  className="size-2.5 shrink-0 self-center rounded-sm"
                                  style={{ backgroundColor: segment.color }}
                                  aria-hidden="true"
                                />
                                <span className="min-w-0 leading-snug text-text-muted">
                                  {segment.name}
                                </span>
                                <span className="flex-1 border-b border-dotted border-surface-border" />
                                <span className="w-16 shrink-0 whitespace-nowrap text-right font-extrabold tabular-nums">
                                  {fmt(segment.count)}
                                </span>
                                <span className="w-12 shrink-0 text-right text-xs tabular-nums text-text-faint">
                                  {msbSegmentsSum > 0
                                    ? `${fmt1((segment.count / msbSegmentsSum) * 100)}%`
                                    : '—'}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              </ModuleGate>

            </>
          )}
        </>
      )}
    </section>
  );
};
